// Client/src/utils/stateLrs/lrsGate.js
// The LRS gate: decides WHICH state's LRS a project (or a single map click)
// should use, now that more than one is registered.
//
// Resolution order:
//   1. an explicit per-project override, when it names a registered state;
//   2. auto-detection from the project's own geometry;
//   3. null — "this project is in a state we have no LRS for", which is a
//      normal outcome, not an error. Most of the 767 projects (Utah, Colorado,
//      Florida, Texas…) land here and must simply not show LRS features.
//
// Auto-detection is a coarse bounding-box test. That is honest for the states
// registered today — Alabama and Massachusetts are ~1,100 miles apart, so a box
// cannot confuse them. It is NOT sufficient once two ADJACENT states are both
// registered; at that point this must become a real point-in-polygon test
// against state boundaries. The override exists so a mis-detection is always
// correctable without a deploy.

import { getLrsAdapter, isLrsStateSupported } from './registry';

/** WGS84 bounding boxes for the states we have adapters for. */
export const LRS_STATE_BOUNDS = {
  AL: { minLat: 30.144, maxLat: 35.009, minLng: -88.474, maxLng: -84.888 },
  MA: { minLat: 41.187, maxLat: 42.887, minLng: -73.509, maxLng: -69.858 },
};

// Roughly 5.5 km of slack, so a corridor that runs right along a state line
// still resolves instead of falling off the edge of its own box.
const BOUNDS_PAD_DEGREES = 0.05;

// Cap on how many vertices we sample from a project's geometry. Detection only
// needs to know which state we are in; walking a 20,000-point lane to decide
// that would be pure waste.
const MAX_SAMPLE_POINTS = 200;

function isFinitePoint(p) {
  return !!p && Number.isFinite(p.lat) && Number.isFinite(p.lng);
}

function withinBounds(point, bounds, pad = BOUNDS_PAD_DEGREES) {
  return point.lat >= bounds.minLat - pad
    && point.lat <= bounds.maxLat + pad
    && point.lng >= bounds.minLng - pad
    && point.lng <= bounds.maxLng + pad;
}

/** Which registered state contains this point, or null. When boxes overlap
 *  (they don't today) the first match wins — see the polygon caveat above. */
export function stateForPoint(point) {
  if (!isFinitePoint(point)) return null;
  for (const [code, bounds] of Object.entries(LRS_STATE_BOUNDS)) {
    if (withinBounds(point, bounds)) return code;
  }
  return null;
}

/** Pull lat/lng samples out of whatever geometry a project happens to have:
 *  path waypoints first (cheap, always present on a drawn route), falling back
 *  to the GeoJSON-ish pathData geometry ([lng, lat] pairs) when a path was
 *  generated rather than hand-placed. */
export function collectSamplePoints(paths, limit = MAX_SAMPLE_POINTS) {
  const out = [];
  for (const path of paths ?? []) {
    for (const wp of path?.waypoints ?? []) {
      if (isFinitePoint(wp)) out.push({ lat: wp.lat, lng: wp.lng });
      if (out.length >= limit) return out;
    }
    const coords = path?.pathData?.geometry?.coordinates;
    if (Array.isArray(coords)) {
      for (const c of coords) {
        if (Array.isArray(c) && Number.isFinite(c[0]) && Number.isFinite(c[1])) {
          out.push({ lat: c[1], lng: c[0] });
        }
        if (out.length >= limit) return out;
      }
    }
  }
  return out;
}

/** Plurality vote over sample points. A tie resolves to null rather than
 *  picking arbitrarily — an ambiguous project should prompt for an override,
 *  not silently query the wrong state. */
export function detectLrsState(points) {
  const counts = new Map();
  for (const p of points ?? []) {
    const code = stateForPoint(p);
    if (code) counts.set(code, (counts.get(code) ?? 0) + 1);
  }
  if (counts.size === 0) return null;
  const ranked = [...counts.entries()].sort((a, b) => b[1] - a[1]);
  if (ranked.length > 1 && ranked[0][1] === ranked[1][1]) return null;
  return ranked[0][0];
}

/**
 * Resolve the LRS for a project.
 *
 * @param {object}   args
 * @param {string?}  args.override  per-project state code, if one is set
 * @param {Array?}   args.paths     the project's paths (for auto-detection)
 * @param {Array?}   args.points    explicit lat/lng samples, instead of paths
 * @returns {{
 *   state: string|null, adapter: object|null,
 *   source: 'override'|'detected'|null,
 *   detected: string|null, overrideIgnored: boolean, reason: string
 * }}
 */
export function resolveLrs({ override = null, paths = null, points = null } = {}) {
  const samples = points ?? collectSamplePoints(paths);
  const detected = detectLrsState(samples);

  if (override) {
    if (isLrsStateSupported(override)) {
      const state = String(override).trim().toUpperCase();
      return {
        state,
        adapter: getLrsAdapter(state),
        source: 'override',
        detected,
        overrideIgnored: false,
        reason: detected && detected !== state
          ? `Set manually to ${state}; this project's geometry looks like ${detected}.`
          : `Set manually to ${state}.`,
      };
    }
    // A stale or mistyped override must not strand the project — fall through
    // to detection and say so, rather than silently honouring nothing.
    if (detected) {
      return {
        state: detected,
        adapter: getLrsAdapter(detected),
        source: 'detected',
        detected,
        overrideIgnored: true,
        reason: `No LRS is registered for "${override}"; using ${detected}, detected from this project's geometry.`,
      };
    }
    return {
      state: null,
      adapter: null,
      source: null,
      detected: null,
      overrideIgnored: true,
      reason: `No LRS is registered for "${override}".`,
    };
  }

  if (detected) {
    return {
      state: detected,
      adapter: getLrsAdapter(detected),
      source: 'detected',
      detected,
      overrideIgnored: false,
      reason: `Detected from this project's geometry.`,
    };
  }

  return {
    state: null,
    adapter: null,
    source: null,
    detected: null,
    overrideIgnored: false,
    reason: samples.length
      ? 'This project is outside every state we have an LRS for.'
      : 'No route geometry yet — draw a route or set the state manually.',
  };
}

/** Convenience wrapper for callers that only need the adapter (or null). */
export function resolveLrsAdapter(args) {
  return resolveLrs(args).adapter;
}

/** Single-point resolution, for the map-click annotation: the clicked location
 *  is itself the best possible sample, so no project geometry is needed. */
export function adapterForPoint(point, override = null) {
  return resolveLrs({ override, points: isFinitePoint(point) ? [point] : [] }).adapter;
}
